import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { BlurImage } from "@/components/BlurImage";
import { resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";

export default function Services({ services, content = {}, onBook }) {
  const [active, setActive] = useState(0);
  const refs = useRef([]);
  const items = services.filter((s) => (s.section || "services") === "services");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.idx));
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, [services]);

  if (!items.length) return null;

  return (
    <section id="services" data-testid="services-section" className="bg-upland-offwhite py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-upland-brown mb-5">{content.services_overline || "What we do"}</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-upland-green max-w-2xl leading-tight">
            {content.services_title || "Senior counsel across the moments that matter most."}
          </h2>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Sticky image — stacked crossfade (opacity only) for smooth, jitter-free transitions */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-32 h-[70vh] overflow-hidden border border-upland-brown/20 relative">
              {items.map((s, i) => (
                <img
                  key={s.id}
                  src={resolveImg(s.image_url)}
                  alt={s.title}
                  loading="lazy"
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover kenburns transition-opacity duration-500 ease-in-out ${
                    active === i ? "opacity-100 kenburns-active" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute bottom-0 inset-x-0 p-8 bg-gradient-to-t from-upland-green/90 to-transparent">
                <span className="font-serif text-2xl text-upland-creme transition-opacity duration-500">{items[active]?.tagline}</span>
              </div>
            </div>
          </div>

          {/* Scrolling content */}
          <div className="lg:col-span-7 space-y-24 lg:space-y-40">
            {items.map((s, i) => (
              <div
                key={s.id}
                data-idx={i}
                ref={(el) => (refs.current[i] = el)}
                data-testid={`service-block-${i}`}
                className={`transition-opacity duration-500 ${active === i ? "opacity-100" : "lg:opacity-40"}`}
              >
                <div className="lg:hidden overflow-hidden mb-6 border border-upland-brown/20">
                  <BlurImage src={s.image_url} alt={s.title} placeholder={s.image_placeholder}
                    className={`w-full h-64 object-cover kenburns ${active === i ? "kenburns-active" : ""}`} />
                </div>
                <span className="text-sm text-upland-brown tracking-wide">0{i + 1}</span>
                <h3 className="font-serif text-3xl md:text-4xl text-upland-green mt-2 mb-5">{s.title}</h3>
                <p className="text-upland-grey text-lg leading-relaxed max-w-xl">{s.description}</p>
                <ul className="mt-7 space-y-3">
                  {s.bullets?.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-3 text-upland-green">
                      <Check size={18} className="mt-1 text-upland-brown shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
